import { useEffect, useMemo, useRef, useState } from 'react';
import { Header, Form, Message } from 'semantic-ui-react';
import { useMutation } from 'react-query';
import { Button } from '~/components';
import { getInvestors } from '~/api/admin';
import {
  getBuyerProfileQuestions,
  scoreBuyerProfileMatch,
} from '~/api/buyerProfile';
import type { MatchResult, QuestionConfig } from './types';
import HeaderScore from './match/HeaderScore';
import SpiderChart from './match/SpiderChart';
import CategoryBreakdownTable from './match/CategoryBreakdownTable';
import NotesSection from './match/NotesSection';
import CollapsibleSection from './match/CollapsibleSection';
import AllInvestorsMatchTable from './match/AllInvestorsMatchTable';

type Props = {
  /** Hashid of the deal shown on the bidding tab — exactly one is set. */
  portfolioId?: string;
  projectId?: string;
};

/**
 * Admin-only panel on the bidding tab: pick an investor and score their buyer
 * profile against this deal (Phase 3). Compute-on-demand — nothing persisted.
 *
 * Renders the engine's `MatchResult` as a score chip + exclusion chip,
 * a spider chart over the seven categories, a category breakdown table,
 * and a collapsible per-question accordion for the math.
 */
export default function BuyerProfileMatchPanel({ portfolioId, projectId }: Props) {
  const [allInvestors, setAllInvestors] = useState<any[]>([]);
  const [questionConfig, setQuestionConfig] = useState<QuestionConfig | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [result, setResult] = useState<MatchResult | null>(null);

  // Build the same shape DealExclusionCard uses so deal-context annotations
  // come back populated and the request fires once an id is in scope.
  const dealParams = useMemo(
    () =>
      projectId
        ? { projectId }
        : portfolioId
        ? { portfolioId }
        : undefined,
    [projectId, portfolioId],
  );

  const { mutate: fetchInvestors } = useMutation(
    async () => getInvestors(dealParams),
    { onSuccess: (data) => setAllInvestors(data || []) }
  );

  const { mutate: fetchQuestions } = useMutation(
    async () => getBuyerProfileQuestions(),
    { onSuccess: (data) => setQuestionConfig(data) }
  );

  const {
    mutate: runScore,
    status: scoreStatus,
    error: scoreError,
  } = useMutation(
    async () => {
      if (!selectedCompanyId) throw new Error('Select an investor company');
      if (!portfolioId && !projectId) throw new Error('Missing deal context');
      return scoreBuyerProfileMatch({
        investorCompanyId: selectedCompanyId,
        portfolioId,
        projectId,
      });
    },
    { onSuccess: (data) => setResult(data) }
  );

  // Questions config is deal-independent — fetch once.
  const questionsRequested = useRef(false);
  useEffect(() => {
    if (!questionsRequested.current) {
      questionsRequested.current = true;
      fetchQuestions();
    }
  }, [fetchQuestions]);

  // Refetch investors when the deal id arrives (initial render may be undefined).
  useEffect(() => {
    if (dealParams) fetchInvestors();
  }, [dealParams, fetchInvestors]);

  /** One option per distinct investor company. */
  const investorOptions = useMemo(() => {
    const seen = new Set<string>();
    return allInvestors
      .map((investor: any) => ({
        key: investor.company?.id,
        value: investor.company?.id,
        text: investor.company?.name,
      }))
      .filter(
        (option: any) =>
          option.value &&
          option.text &&
          !seen.has(option.value) &&
          seen.add(option.value)
      );
  }, [allInvestors]);

  const labelFor = (questionKey: string): string =>
    questionConfig?.questions.find((q) => q.key === questionKey)?.label ??
    questionKey;

  const categoryFor = (questionKey: string) =>
    questionConfig?.questions.find((q) => q.key === questionKey)?.category;

  return (
    <div className="card buyer-profile-match">
      <Header as="h2">Buyer Profile Match</Header>

      <CollapsibleSection title="Score a single investor">
      <p className="buyer-profile-match__blurb">
        Score an investor&rsquo;s buyer profile against this deal. Computed
        on demand from the latest profile answers &mdash; nothing is saved.
      </p>
      <Form>
        <Form.Group>
          <Form.Dropdown
            selection
            search
            width={10}
            label="Investor company"
            placeholder="Select an investor company…"
            options={investorOptions}
            value={selectedCompanyId}
            onChange={(_e, { value }) =>
              setSelectedCompanyId(String(value ?? ''))
            }
          />
          <Form.Field width={3} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              primary
              loading={scoreStatus === 'loading'}
              disabled={!selectedCompanyId || scoreStatus === 'loading'}
              onClick={() => {
                setResult(null);
                runScore();
              }}
            >
              Score match
            </Button>
          </Form.Field>
        </Form.Group>
      </Form>

      {scoreError ? (
        <Message negative size="small">
          {(scoreError as Error)?.message || 'Could not score this deal.'}
        </Message>
      ) : null}

      {result ? (
        <div className="buyer-profile-match__result">
          <div className="buyer-profile-match__body">
            <div className="buyer-profile-match__overview">
              <HeaderScore
                score={result.score}
                exclusionTotal={result.exclusionTotal}
                exclusionBand={result.exclusionBand}
                killed={result.killed}
              />
              <div className="buyer-profile-match__spider-card">
                <SpiderChart categories={result.categories} />
              </div>
              <div className="buyer-profile-match__counts">
                {result.contributions.length} field
                {result.contributions.length === 1 ? '' : 's'} scored
                {result.skipped.length
                  ? `, ${result.skipped.length} skipped`
                  : ''}
              </div>
            </div>
            <div className="buyer-profile-match__detail">
              <div className="buyer-profile-match__table-card">
                <CategoryBreakdownTable
                  categories={result.categories}
                  contributions={result.contributions}
                  exclusions={result.exclusions}
                  skipped={result.skipped}
                  labelFor={labelFor}
                  categoryFor={categoryFor}
                />
              </div>
              <NotesSection signals={result.signals} />
            </div>
          </div>
        </div>
      ) : null}
      </CollapsibleSection>

      <CollapsibleSection title="All investors">
      <AllInvestorsMatchTable
        projectId={projectId}
        portfolioId={portfolioId}
        labelFor={labelFor}
        categoryFor={categoryFor}
      />
      </CollapsibleSection>
    </div>
  );
}
